import { useState, useEffect, useCallback } from "react";
import { SidebarContext } from "./SidebarContext";

// The therapist's own choice of nav width, kept across reloads. Only an
// explicit toggle writes it: screens that collapse the nav for room (the
// constellation board, the live-session wizard) must not silently rewrite the
// preference, so they call `collapseSidebar` instead.
//
// Colon-separated rather than dot-separated on purpose: the i18n guard in
// src/shared/i18n scans every source file for quoted dotted paths beginning
// with a locale namespace and requires them to exist in both bundles, and
// "sidebar" is one. A storage key is not a translation key.
const EXPANDED_KEY = "sidebar:expanded";

function readExpanded(): boolean {
    try {
        return localStorage.getItem(EXPANDED_KEY) !== "false"; // default open
    } catch {
        return true;
    }
}

function persistExpanded(next: boolean): void {
    try {
        localStorage.setItem(EXPANDED_KEY, String(next));
    } catch {
        /* private mode / quota — the nav still works, it just won't be remembered */
    }
}

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(readExpanded);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsExpanded((prev) => {
      persistExpanded(!prev);
      return !prev;
    });
  }, []);

  // Collapse for a screen that needs the width. Deliberately not persisted:
  // leaving that screen restores whatever the therapist had chosen.
  const collapseSidebar = useCallback(() => setIsExpanded(false), []);

  // Put the nav back to the stored preference once such a screen is left.
  const restoreSidebar = useCallback(() => setIsExpanded(readExpanded()), []);

  const toggleMobileSidebar = () => {
    setIsMobileOpen((prev) => !prev);
  };

  const toggleSubmenu = (item: string) => {
    setOpenSubmenu((prev) => (prev === item ? null : item));
  };

  return (
    <SidebarContext.Provider
      value={{
        isExpanded: isMobile ? false : isExpanded,
        isMobileOpen,
        isHovered,
        activeItem,
        openSubmenu,
        toggleSidebar,
        collapseSidebar,
        restoreSidebar,
        toggleMobileSidebar,
        setIsHovered,
        setActiveItem,
        toggleSubmenu,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
